import React, { useEffect, useRef, useState } from "react";
import { useInput } from "react-admin";
import TextField from "@material-ui/core/TextField";

export const FONT_FIELDS = [
    {
        source: "customization.primaryFont",
        label: "Primary Font",
        info: "Sets the font for sidebar step names (e.g. Category, Material), tab headings, and category section titles (e.g. Precious Metal).",
    },
    {
        source: "customization.secondaryFont",
        label: "Secondary Font",
        info: "Sets the font for selected values shown in the sidebar (e.g. 14K White Gold) and option labels under each choice in the grid.",
    },
];

export const FONT_OPTIONS = [
    { label: "Reckless", value: "Reckless" },
    { label: "Neue Haas Unica Pro", value: "Neue Haas Unica Pro" },
    { label: "HK Grotesk", value: "HK Grotesk" },
    { label: "Montserrat", value: "Montserrat" },
    { label: "Tenor Sans", value: "Tenor Sans" },
    { label: "Futura PT", value: "futura-pt,sans-serif" },
    { label: "Roboto", value: "Roboto" },
    { label: "Cormorant Garamond", value: "Cormorant Garamond" },
    { label: "Lato", value: "Lato" },
    { label: "Nanum Myeongjo", value: "Nanum Myeongjo, serif" },
    { label: "Barlow", value: "Barlow, sans-serif" },
];

export const COLOR_FIELDS = [
    { source: "customization.brandColor", label: "Brand Color" },
    {
        source: "customization.primaryColor",
        label: "Primary Color",
        info: "Sets the color for sidebar step names (e.g. Category, Material), tab headings, and category section titles (e.g. Precious Metal).",
    },
    {
        source: "customization.secondaryColor",
        label: "Secondary Color",
        info: "Sets the color for selected values shown in the sidebar (e.g. 14K White Gold) and option labels under each choice in the grid.",
    },
    { source: "customization.tertiaryColor", label: "Tertiary Color" },
    {
        source: "customization.primaryBgColor",
        label: "Primary Background Color",
        info: "Sets the background color of the sidebar.",
    },
    { source: "customization.secondaryBgColor", label: "Secondary Background Color" },
    {
        source: "customization.primaryBorderColor",
        label: "Primary Border Color",
        info: "Sets the border on the selected sidebar item and the divider between the gallery and main content area.",
    },
    { source: "customization.secondaryBorderColor", label: "Secondary Border Color" },
];

export const BORDER_RADIUS_FIELDS = [
    { source: "customization.primaryBorderRadius", label: "Primary Border Radius(in px)" },
    { source: "customization.secondaryBorderRadius", label: "Secondary Border Radius(in px)" },
    { source: "customization.tertiaryBorderRadius", label: "Tertiary Border Radius(in px)" },
];

export const BOOLEAN_CONFIG_FIELDS = [
    { source: "customization.showShippingEstimation", label: "Show Shipping Estimation" },
    { source: "customization.enableDefaultDone", label: "Enable Default Done" },
    { source: "customization.enablePoweredBy", label: "Enable Powered By" },
    { source: "customization.enableMagicNoSearch", label: "Enable Magic No Search" },
];

export const validateMax255 = (value) => {
    if (value == null || value === "") return undefined;
    return String(value).length <= 255
        ? undefined
        : "Must be 255 characters or less";
};

export const validateHexColor = (value) =>
    /^#[0-9A-Fa-f]{6}$/.test(value || "")
        ? undefined
        : "Enter a valid hex color (#RRGGBB)";

export const normalizeHexValue = (value) => {
    if (!value) return "";
    const trimmed = String(value).trim();
    const prefixed = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
    return prefixed.toUpperCase();
};

export const validateBorderRadius = (value) => {
    if (value == null || value === "") return undefined;
    return /^\d+(?:\.\d+)?(?:px)?$/i.test(String(value).trim())
        ? undefined
        : "Enter a number or a value ending in px";
};

export const normalizeBorderRadius = (value) => {
    if (value == null || value === "") return null;
    const trimmed = String(value).trim();

    if (/^\d+(?:\.\d+)?$/i.test(trimmed)) {
        return `${trimmed}px`;
    }

    if (/^\d+(?:\.\d+)?px$/i.test(trimmed)) {
        return `${trimmed.replace(/px$/i, "")}px`;
    }

    return trimmed;
};

export const parseBorderRadius = (value) => {
    return normalizeBorderRadius(value);
};

export const formatBorderRadius = (value) => {
    if (value == null || value === "") return "";
    const trimmed = String(value).trim();
    if (/^\d+(?:\.\d+)?px$/i.test(trimmed)) {
        return trimmed.replace(/px$/i, "");
    }
    return trimmed;
};

export const BorderRadiusInput = ({ source, label = false, helperText = false, fullWidth = true }) => {
    const { input, meta } = useInput({ source, validate: validateBorderRadius });
    const [displayValue, setDisplayValue] = useState("");
    const pauseTimerRef = useRef(null);
    const syncingFromInputRef = useRef(true);

    useEffect(() => {
        if (!syncingFromInputRef.current) return;
        const normalized = normalizeBorderRadius(input.value);
        setDisplayValue(normalized == null ? "" : String(normalized));
    }, [input.value]);

    useEffect(() => {
        return () => {
            if (pauseTimerRef.current) {
                clearTimeout(pauseTimerRef.current);
            }
        };
    }, []);

    const applyNormalizedValue = (rawValue) => {
        const normalized = normalizeBorderRadius(rawValue);
        input.onChange(normalized);
        syncingFromInputRef.current = true;
        setDisplayValue(normalized == null ? "" : String(normalized));
    };

    const handleChange = (event) => {
        const nextValue = event.target.value;
        syncingFromInputRef.current = false;
        setDisplayValue(nextValue);
        input.onChange(nextValue === "" ? null : nextValue);

        if (pauseTimerRef.current) {
            clearTimeout(pauseTimerRef.current);
        }

        pauseTimerRef.current = setTimeout(() => {
            applyNormalizedValue(nextValue);
        }, 700);
    };

    const handleBlur = () => {
        if (pauseTimerRef.current) {
            clearTimeout(pauseTimerRef.current);
        }
        applyNormalizedValue(displayValue);
        input.onBlur();
    };

    const hasError = (meta.touched || meta.submitFailed) && (meta.error || meta.submitError);

    return (
        <TextField
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={input.onFocus}
            label={label || undefined}
            error={Boolean(hasError)}
            helperText={hasError ? meta.error || meta.submitError : helperText || ""}
            fullWidth={fullWidth}
            inputProps={{
                inputMode: "decimal",
                pattern: "[0-9]*[.]?[0-9]+(px)?",
            }}
        />
    );
};
